<script setup>
import { computed, onMounted, ref, nextTick } from 'vue'
import { useCartStore } from '../stores/cartStore'
import { useTeamStore } from '../stores/teamStore'
import { useUserStore } from '../stores/userStore'
import TheHero from './TheHero.vue'
import CartPaymentStripeEmbed from './CartPaymentStripeEmbed.vue'
import CartPaymentOrderSummary from './CartPaymentOrderSummary.vue'
import CartPaymentOrderSummaryV2 from './CartPaymentOrderSummaryV2.vue'
import CartPaymentOrderSummaryV3 from './CartPaymentOrderSummaryV3.vue'
import CartPaymentOrderSummaryV4 from './CartPaymentOrderSummaryV4.vue'
import CartPaymentScreenInfoBlock from './CartPaymentScreenInfoBlock.vue'
import CartPaymentMethodsForm from './CartPaymentMethodsForm.vue'
import CartStepAuth from './CartStepAuth.vue'
import LoginByCode from './auth/LoginByCode.vue'
import CardLayout from './CardLayout.vue'
import { backToTop } from '../stores/storeHelpers'
import { confirmPaymentWithExistingMethod } from '../helpers/payments'

// State and store initialization
const cartStore = useCartStore()
const teamStore = useTeamStore()
const userStore = useUserStore()

// Get client secret from the store
const clientSecret = computed(() => cartStore.stripeClientSecret)

const annualReportRenewalFlag = computed(() => {
  return (
    (typeof window !== 'undefined' &&
      window.waffle &&
      typeof window.waffle.switch_is_active === 'function' &&
      window.waffle.switch_is_active('annual_reports_renewal')) ||
    false
  )
})

const showAnnualReportRenewal = computed(() => {
  return (
    cartStore.subscriptionStatus?.active &&
    annualReportRenewalFlag &&
    cartStore.cartData?.cartConfig?.reportType === 'annual_report' &&
    userStore.isAuthenticated
  )
})

const isCardExpiringThisYear = computed(
  () => teamStore.isDefaultPaymentMethodExpiringThisYear
)

const showStripeEmbed = ref(false)

const shouldShowStripeEmbed = computed(() => {
  return (
    showStripeEmbed.value || teamStore.isDefaultPaymentMethodExpiringThisYear
  )
})

onMounted(async () => {
  backToTop()

  // Load team payment methods if we have a team slug
  if (cartStore.cartData?.initialFormData?.corporation_name) {
    const teamSlug = cartStore.cartData.initialFormData.corporation_name
      .toLowerCase()
      .replace(/\s+/g, '-')
    await teamStore.getTeamPaymentMethods(teamSlug)
  }

  await nextTick()
})

const heroContent = computed(() => {
  if (cartStore.cartData.cartConfig.orderSummary == 'V4') {
    return {
      title: 'Urgent: Filing Required!',
      subtitle:
        "Based on the information you provided, your company is required to file under the Corporate Transparency Act. Don't worry, your in the right place to ensure your compliance is handled quickly and accurately."
    }
  } else {
    return {
      title: 'One Last Step',
      subtitle:
        'Please review your order below and submit your payment information.'
    }
  }
})

const activeSubHeroContent = computed(() => {
  return {
    title: 'Log In To Continue',
    subtitle:
      'This business already has an active subscription. Is there another email you may have associated with your account?'
  }
})

const activeSubHeroWithProductsContent = computed(() => {
  return {
    title: 'One Last Step',
    subtitle:
      'Please select an existing payment method or add a new one to pay the Florida State filing fee. This fee will be paid directly to the state of Florida when we file your report.'
  }
})

const nextUrl = computed(() => {
  return `/accounts/login/code/?next=/a/${cartStore.subscriptionStatus.slug}/`
})

const paymentMethods = computed(() => {
  return teamStore.currentTeam?.paymentMethods || []
})

const isProcessing = ref(false)
const errorMessage = ref('')

const handlePaymentConfirmation = async (values) => {
  if (!clientSecret.value) {
    errorMessage.value = 'Missing payment intent. Please try again.'
    return
  }

  if (!values.payment_method) {
    errorMessage.value = 'Please select a payment method.'
    return
  }

  isProcessing.value = true
  errorMessage.value = ''

  try {
    const { error, paymentIntent } = await confirmPaymentWithExistingMethod(
      clientSecret.value,
      values.payment_method,
      cartStore
    )

    if (error) {
      errorMessage.value = error.message
      return
    }

    // Payment successful - redirect to success page
    window.location.href = `/cart/success/?payment_intent=${paymentIntent.id}`
  } catch (e) {
    console.error('Error processing payment:', e)
    errorMessage.value = 'An unexpected error occurred. Please try again.'
  } finally {
    isProcessing.value = false
  }
}

const handleAddNewCard = () => {
  showStripeEmbed.value = true
}
</script>

<template>
  <div v-if="!cartStore.subscriptionStatus?.active">
    <TheHero :content="heroContent" />
    <div class="card">
      <div class="card-body">
        <div class="row justify-content-center">
          <div
            v-if="cartStore.cartData.cartConfig.orderSummary == 'V4'"
            class="col-12"
          >
            <CartPaymentScreenInfoBlock />
          </div>
          <div
            :class="{
              'col-md-7 col-lg-8 order-2':
                cartStore.cartData.cartConfig.paymentScreen == 'elements' &&
                cartStore.cartData.cartConfig.orderSummaryLayout == 'R'
            }"
          >
            <CartPaymentStripeEmbed :clientSecret="clientSecret" />
          </div>
          <div class="col-md-5 col-lg-4 order-1 order-md-2">
            <CartPaymentOrderSummary
              v-if="
                cartStore.cartData.cartConfig.paymentScreen != 'checkout' &&
                cartStore.cartData.cartConfig.orderSummary == 'V1'
              "
            />
            <CartPaymentOrderSummaryV2
              v-if="
                cartStore.cartData.cartConfig.paymentScreen != 'checkout' &&
                cartStore.cartData.cartConfig.orderSummary == 'V2'
              "
            />
            <CartPaymentOrderSummaryV3
              v-if="
                cartStore.cartData.cartConfig.paymentScreen != 'checkout' &&
                cartStore.cartData.cartConfig.orderSummary == 'V3'
              "
            />
            <CartPaymentOrderSummaryV4
              v-if="
                cartStore.cartData.cartConfig.paymentScreen != 'checkout' &&
                cartStore.cartData.cartConfig.orderSummary == 'V4'
              "
            />
          </div>
        </div>
      </div>
    </div>

    <template v-if="cartStore.cartData.cartConfig.includeDisclaimer">
      <div class="mt-5">
        <small class="text-muted mt-10">
          IMPORTANT NOTICE: You are NOT required to purchase anything from this
          company and the company is NOT affiliated, endorsed, or approved by
          any governmental entity. The item offered in this advertisement has
          NOT been approved or endorsed by any governmental agency, and this
          offer is NOT being made by an agency of the government.
        </small>
      </div>
    </template>
  </div>
  <div v-else-if="showAnnualReportRenewal">
    <TheHero :content="activeSubHeroWithProductsContent" />

    <CardLayout includeHeader="true" title="Confirm Your Payment Details">
      <div class="row justify-content-center">
        <div
          :class="{
            'col-md-7 col-lg-8 order-2':
              cartStore.cartData.cartConfig.paymentScreen == 'elements' &&
              cartStore.cartData.cartConfig.orderSummaryLayout == 'R'
          }"
        >
          <template v-if="shouldShowStripeEmbed">
            <CartPaymentStripeEmbed :client-secret="clientSecret" />
          </template>
          <template v-else>
            <CartPaymentMethodsForm
              :client-secret="clientSecret"
              :payment-methods="paymentMethods"
              @edit="handleEdit"
              @delete="handleDelete"
              @add="handleAdd"
            />
            <div class="mt-3 text-center">
              <a href="#" @click.prevent="handleAddNewCard">
                Add a new payment method
              </a>
            </div>
          </template>
        </div>
        <div class="col-md-5 col-lg-4 order-1 order-md-2">
          <CartPaymentOrderSummaryV3 />
        </div>
      </div>
    </CardLayout>
  </div>
  <!-- If there is an active sub for this business -->
  <div v-else>
    <!-- Hero -->
    <div class="text-center mx-auto mb-5">
      <!-- Heading -->
      <div class="mb-5">
        <h1 class="display-4 mb-3">{{ activeSubHeroContent.title }}</h1>
        <p v-if="activeSubHeroContent.subtitle" class="lead fs-2">
          {{ activeSubHeroContent.subtitle }}
        </p>
        <LoginByCode v-if="!userStore.isAuthenticated" />
      </div>
      <!-- End Heading -->
    </div>
    <!-- end hero-->
  </div>
</template>
